import {
	getDisplayName,
	getInitials,
	type SearchEventResult,
	type SearchMeetingResult,
	type SearchMemberResult,
	type SearchPostResult,
	type SearchResponse,
} from "@upsquad/shared";
import { Calendar, FileText, Users, Video } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Avatar, Spinner } from "@/src/common/components/ui";
import { formatDate } from "@/src/common/lib/date.utils";
import { EVENT_ROUTES } from "@/src/event/constants/event-routes";
import { MEETING_ROUTES } from "@/src/meeting/constants/meeting-routes";
import { MEMBER_ROUTES } from "@/src/member/constants/member-routes";
import { POST_ROUTES } from "@/src/post/constants/post-routes";
import { SearchResultRow } from "./search-result-row";
import { SearchSection } from "./search-section";

interface SearchResultsListProps {
	results?: SearchResponse;
	isLoading: boolean;
	onSelect: () => void;
}

export function SearchResultsList({ results, isLoading, onSelect }: SearchResultsListProps) {
	const { t } = useTranslation("search");
	const navigate = useNavigate();

	if (isLoading && !results) {
		return (
			<div className="flex items-center justify-center gap-2 px-3 py-6 text-sm text-foreground/60">
				<Spinner size="sm" />
				<span>{t("loading")}</span>
			</div>
		);
	}

	if (!results) return null;

	const handleMember = (m: SearchMemberResult) => {
		navigate(MEMBER_ROUTES.DETAIL.replace(":userId", m.user_id));
		onSelect();
	};
	const handlePost = (p: SearchPostResult) => {
		navigate(POST_ROUTES.DETAIL.replace(":postId", p.id));
		onSelect();
	};
	const handleMeeting = (m: SearchMeetingResult) => {
		navigate(MEETING_ROUTES.DETAIL.replace(":meetingId", m.id));
		onSelect();
	};
	const handleEvent = (e: SearchEventResult) => {
		navigate(EVENT_ROUTES.DETAIL.replace(":eventId", e.id));
		onSelect();
	};

	return (
		<div className="max-h-[420px] overflow-y-auto">
			<SearchSection title={t("sections.members")} icon={<Users size={12} />} isEmpty={results.members.length === 0}>
				{results.members.map((member) => {
					const displayName = getDisplayName(member.first_name, member.last_name);
					return (
						<SearchResultRow
							key={member.user_id}
							icon={
								<Avatar size="sm">
									<Avatar.Image src={member.photo_url ?? undefined} alt={displayName} />
									<Avatar.Fallback>{getInitials(displayName)}</Avatar.Fallback>
								</Avatar>
							}
							title={displayName}
							subtitle={member.role}
							onSelect={() => handleMember(member)}
						/>
					);
				})}
			</SearchSection>

			<SearchSection title={t("sections.posts")} icon={<FileText size={12} />} isEmpty={results.posts.length === 0}>
				{results.posts.map((post) => {
					const author = getDisplayName(post.author_first_name, post.author_last_name);
					const title = post.content_preview || t("untitled_post");
					return (
						<SearchResultRow
							key={post.id}
							icon={<FileText size={14} className="text-muted" />}
							title={title}
							subtitle={`${author} · ${formatDate(post.created_at)}`}
							onSelect={() => handlePost(post)}
						/>
					);
				})}
			</SearchSection>

			<SearchSection title={t("sections.meetings")} icon={<Video size={12} />} isEmpty={results.meetings.length === 0}>
				{results.meetings.map((meeting) => (
					<SearchResultRow
						key={meeting.id}
						icon={<Video size={14} className="text-muted" />}
						title={meeting.title}
						subtitle={formatDate(meeting.start_at)}
						onSelect={() => handleMeeting(meeting)}
					/>
				))}
			</SearchSection>

			<SearchSection title={t("sections.events")} icon={<Calendar size={12} />} isEmpty={results.events.length === 0}>
				{results.events.map((event) => (
					<SearchResultRow
						key={event.id}
						icon={<Calendar size={14} className="text-muted" />}
						title={event.title}
						subtitle={formatDate(event.start_at)}
						onSelect={() => handleEvent(event)}
					/>
				))}
			</SearchSection>
		</div>
	);
}
